import React, { memo, useCallback } from 'react';
import { connect } from 'react-redux';
import { List } from '@mui/material';
import { createActions } from '../../../../store';
import * as PIXI from 'pixi.js';
import CollapsibleSwitchListItem from '../../../../../../list-items/CollapsibleSwitchListItem';
import CreatePageConstants, { DEFAULT_LAYER_FIRE_SETTINGS } from '../../../../CreatePageConstants';
import SliderListItem from '../../../../../../list-items/SliderListItem';
import CondensedListItem from '../../../../../../list-items/CondensedListItem';
import { previewHistoryBatch } from '../../../../store/extensions';

const WaveLayerFireListItem = memo(({
    index,
    enabled,
    fireSettings,
    updateWaveLayer,
    maxFireIntensity,
    maxFireDetail
}) => {
    const { intensity, detail } = fireSettings;

    const onFireEnabledChange = useCallback((value) => {
        updateWaveLayer(index, { fireSettings: { enabled: value } });
    }, [updateWaveLayer, index]);

    const onFireIntensityChange = useCallback((value) => {
        previewHistoryBatch.start();
        updateWaveLayer(index, { fireSettings: { intensity: value } });
    }, [updateWaveLayer, index]);

    const onFireDetailChange = useCallback((value) => {
        previewHistoryBatch.start();
        value = maxFireDetail - Math.sqrt(value);
        updateWaveLayer(index, { fireSettings: { detail: value } });
    }, [updateWaveLayer, index, maxFireDetail]);

    return (
        <CollapsibleSwitchListItem
            checked={enabled}
            text='Fire'
            onChange={onFireEnabledChange}
            warning={PIXI.utils.isWebGLSupported() ? null : CreatePageConstants.LIMITED_VISIBILITY_WARNING_TEXT}
            dense={true}
        >
            <List dense disablePadding>
                <CondensedListItem
                    elements={[
                        {
                            width: 6,
                            component: (
                                <SliderListItem
                                    title='Intensity'
                                    value={intensity}
                                    displayMultiplier={100 / maxFireIntensity}
                                    min={0}
                                    max={100}
                                    step={1}
                                    onValueChange={onFireIntensityChange}
                                />
                            )
                        },
                        {
                            width: 6,
                            component: (
                                <SliderListItem
                                    title='Detail'
                                    value={(maxFireDetail - detail) ** 2}
                                    displayMultiplier={100 / (maxFireDetail ** 2)}
                                    min={0}
                                    max={99}
                                    step={1}
                                    onValueChange={onFireDetailChange}
                                />
                            )
                        }
                    ]}
                />
            </List>
        </CollapsibleSwitchListItem>
    );
});

const mapStateToProps = (state, { index }) => {
    const layer = state.createPreview.present.videoSettings.visualizer.waveCircles[index];
    const globalSettings = state.createPreview.present.videoSettings.visualizer.fireSettings;
    const layerFireSettings = layer.customOptions?.fireSettings || DEFAULT_LAYER_FIRE_SETTINGS;

    return {
        enabled: layerFireSettings.enabled,
        fireSettings: layerFireSettings.enabled ? layerFireSettings : globalSettings,
        maxFireIntensity: state.create.maxValues.maxFireIntensity,
        maxFireDetail: state.create.maxValues.maxFireDetail
    };
};

const mapDispatchToProps = {
    updateWaveLayer: (index, properties) => createActions.updateWaveLayer(index, properties)
};

export default connect(mapStateToProps, mapDispatchToProps)(WaveLayerFireListItem);
